import React, { useState, useEffect } from "react";
import { Loader2, PackageOpen, ArrowRight, X, Box } from "lucide-react";
import { Link } from "react-router-dom";
import { http } from "../../axios/axios";
import SectionHeader from "../SectionHeader";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import ProductCard from "../ProductCard";

const activeStyle = "bg-blue-600 text-white shadow-md";

const normalStyle = "bg-slate-100 text-slate-700 hover:bg-slate-200";

const buttonClasstwo = (active) =>
  `
  rounded-md
  transition-all
  duration-200
  font-semibold

  min-w-[160px]
  text-center

  px-5
  py-2.5
  text-sm

  sm:min-w-[180px]
  sm:px-6
  sm:py-3
  sm:text-base

  lg:min-w-[190px]
  lg:px-7
  lg:py-3

  2xl:min-w-[210px]
  2xl:px-8
  2xl:py-3.5
  2xl:text-lg

  ${active ? activeStyle : normalStyle}
  `;

const Partner = () => {
  const [securityBrands, setSecurityBrands] = useState([]);
  const [itBrands, setItBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [loading, setLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    productType: "security",
  });

  const isItProductType = selectedFilters.productType === "it";

  const selectProductType = (type) => {
    setSelectedFilters((prev) => ({
      ...prev,
      productType: type,
    }));

    setSelectedBrandId("");
    setProducts([]);
  };

  const handleProductFetch = async (brandId, productType) => {
    try {
      setProductLoading(true);

      const isIt = productType === "it";

      const res = await http.get("/products/filter", {
        params: {
          catalogType: isIt ? "ip-network" : "catalog",
          ...(isIt ? { ipNetworkBrand: brandId } : { brand: brandId }),
          limit: 4,
        },
      });

      setProducts(res.data.products || []);
    } catch (err) {
      console.log(err);
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  };

  useEffect(() => {
    async function fetchBrands() {
      try {
        const [securityResult, itResult] = await Promise.allSettled([
          http.get("/brands", { params: { limit: 100 } }),
          http.get("/ip-network/brands", { params: { limit: 100 } }),
        ]);

        if (securityResult.status === "rejected") {
          console.log("Brand API Error:", securityResult.reason);
        }

        if (itResult.status === "rejected") {
          console.log("IT Brand API Error:", itResult.reason);
        }

        setSecurityBrands(
          securityResult.status === "fulfilled"
            ? securityResult.value.data?.brands || []
            : [],
        );

        setItBrands(
          itResult.status === "fulfilled"
            ? itResult.value.data?.brands || []
            : [],
        );
      } finally {
        setLoading(false);
      }
    }

    fetchBrands();
  }, []);

  const brands = isItProductType ? itBrands : securityBrands;

  const selectedBrand = brands.find((brand) => brand._id === selectedBrandId);

  const marqueeBrands = [...brands, ...brands];

  if (loading) {
    return (
      <section className="py-32 flex justify-center">
        <Loader2 className="animate-spin text-blue-600" size={38} />
      </section>
    );
  }

  return (
    <section className="w-full py-6 overflow-hidden">
      <style>{`
        @keyframes brandScroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }

        .brand-marquee {
          animation: brandScroll 50s linear infinite;
        }

        .brand-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="px-4 sm:px-6 lg:px-8">
        <SectionHeader
          badge="Trusted Partners"
          title="Authorized Brands"
          description="We collaborate with leading security surveillance and IT product technology brands."
          showLine
        />
        <div
          className="
      flex
      flex-col
      items-center
      text-blue-600
      font-semibold
      text-xs
      sm:text-sm
      pointer-events-none
    "
        >
          <span className="animate-pulse mb-1">Click here to choose</span>
          <span className="text-xl animate-bounce leading-none">↓</span>
        </div>
        <div className="flex justify-center flex-wrap gap-2 md:gap-3 mt-6 mb-8">
          <div className="relative flex flex-wrap gap-2 md:gap-3 py-3"></div>
          <button
            type="button"
            onClick={() => selectProductType("security")}
            className={buttonClasstwo(
              selectedFilters.productType === "security",
            )}
          >
            Security Products
          </button>

          <button
            type="button"
            onClick={() => selectProductType("it")}
            className={buttonClasstwo(selectedFilters.productType === "it")}
          >
            IT Products
          </button>
        </div>

        {brands.length ? (
          <div className="mt-8 overflow-hidden">
            <div
              className="
                flex
                gap-6
                w-max
                items-center
                brand-marquee
              "
            >
              {marqueeBrands.map((brand, index) => (
                <div
                  key={`${brand._id}-${index}`}
                  onClick={() => {
                    if (selectedBrandId === brand._id) {
                      setSelectedBrandId("");
                      setProducts([]);

                      return;
                    }

                    setSelectedBrandId(brand._id);
                    handleProductFetch(brand._id, selectedFilters.productType);
                  }}
                  className="
                    group
                    cursor-pointer
                    h-32
                    md:h-40
                    flex
                    items-center
                    justify-center
                    bg-white
                    rounded
                    border
                    border-slate-200
                    p-6
                    overflow-hidden
                  "
                >
                  <img
                    src={GetImageUrl(brand.brandLogo)}
                    alt={brand.brandName}
                    loading="lazy"
                    className="
                      max-h-full
                      max-w-full
                      object-contain
                      scale-105
                      group-hover:scale-110
                      transition-transform
                      duration-300
                    "
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="
  py-20
  text-center
  border
  border-dashed
  border-slate-300
  rounded-2xl
  bg-slate-50
"
          >
            <Box size={40} className="mx-auto text-slate-300 mb-4" />

            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              No Brands Available
            </h3>

            <p className="text-sm text-slate-500">
              Unable to load partner brands right now.
            </p>
          </div>
        )}

        <div
          className={`mt-8 transition-all duration-300 overflow-hidden 
  ${selectedBrandId ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"}
  `}
        >
          <div
            className="
      bg-slate-100
      border
      border-slate-200
      rounded-2xl
      p-3
      sm:p-5
    "
          >
            <div
              className="
        flex

        sm:flex-row
        sm:items-center
        justify-between
        gap-4
        mb-5
        pb-4
        border-b
        border-b-slate-200
      "
            >
              <div>
                <h3
                  className="
            text-[10px]
            sm:text-xs
            uppercase
            tracking-widest
            text-blue-600
            font-semibold
          "
                >
                  Featured Products
                </h3>

                <h4
                  className="
            text-lg
            sm:text-xl
            font-bold
            text-slate-900
            mt-1

          "
                >
                  {selectedBrand?.brandName}
                </h4>
              </div>

              <div
                className="
          flex
          items-center
          gap-2
          justify-end
        "
              >
                <button
                  onClick={() => {
                    setSelectedBrandId("");
                    setProducts([]);
                  }}
                  className="
            w-9
            h-9
            rounded-full
            border
            border-slate-300
            bg-white
            flex
            items-center
            justify-center
            text-slate-500
            hover:bg-slate-100
            hover:text-red-500
            transition
          "
                >
                  <X size={18} />
                </button>

                <Link
                  to={isItProductType ? "/it-products" : "/security-products"}
                  className="
            hidden
            sm:inline-flex
            items-center
            gap-2
            px-4
            py-2
            rounded-lg
            bg-blue-600
            text-white
            text-sm
            font-semibold
            hover:bg-blue-700
            transition
          "
                >
                  View All Products
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </div>
            </div>

            {productLoading ? (
              <div className="py-14 flex justify-center">
                <Loader2 className="animate-spin text-blue-600" />
              </div>
            ) : products.length ? (
              <div
                className="
          grid
          grid-cols-1
          sm:grid-cols-2
          lg:grid-cols-3
          xl:grid-cols-4
          gap-4
        "
              >
                {products.map((product, index) => (
                  <div
                    key={product._id}
                    className={`
              ${index >= 4 ? "hidden sm:block" : ""}
            `}
                  >
                    <ProductCard item={product} />
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="
          py-10
          flex
          flex-col
          items-center
          text-center
        "
              >
                <PackageOpen size={35} className="text-slate-400" />

                <h3
                  className="
            mt-3
            font-semibold
            text-slate-800
          "
                >
                  No Products Available
                </h3>

                <p className="text-sm text-slate-500">
                  No products found for this brand.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Partner;
